import { useEffect, useRef, useState } from 'react';

// Shared count-up/down hook used by analytics KPI cards and task KPI cards.
// On first mount: 'up' counts 0 → target, 'down' counts ~2×target → target.
// On subsequent value changes: animates from the current displayed value to the new target.
// Ease: cubic ease-out — fast start, natural deceleration.
export default function useCountUp(target, duration = 1200, direction = 'up') {
    const [current, setCurrent] = useState(0);
    const rafRef = useRef(null);
    const startTimeRef = useRef(null);
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
        if (typeof target !== 'number' || Number.isNaN(target)) {
            setCurrent(target || 0);
            return undefined;
        }

        // First animation: 'up' starts from 0, 'down' starts from ~2× target.
        // Subsequent updates: animate from the current displayed value.
        let from;
        if (!hasAnimatedRef.current) {
            from = direction === 'down' ? Math.round(target * 2) : 0;
            hasAnimatedRef.current = true;
        } else {
            from = current;
        }

        startTimeRef.current = null;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - (1 - progress) ** 3;
            setCurrent(Math.round(from + (target - from) * eased));
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            }
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, duration, direction]);

    return current;
}
